import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { LOCAL_STORAGE_KEYS } from '../../enums/local-storage.enum';
import { IBlockPermission, IDistrictPermission, INationalPermission, IStatePermission } from '../../models/login.model';
import { StorageService } from '../storage/storage-service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private readonly http = inject(HttpClient);
    private readonly storageService = inject(StorageService);

    private readonly _activeRole = new BehaviorSubject<string | null>(null);
    private readonly _tenantId = new BehaviorSubject<string | null>(null);
    private readonly keycloakApiBaseUrl = environment.adminLokOSBaseUrl;

    getUserProfileImage() {
        const url = `${this.keycloakApiBaseUrl}/api/keycloak/v2/graph/realms/${environment.keycloak.realm}/image`;
        return this.http.get(url, {
            responseType: 'text',
        });
    }

    setTenantId(value: string) {
        this._tenantId.next(value);
    }

    getTenantId(): string | null {
        return this._tenantId.getValue();
    }

    get activeRole$(): Observable<string | null> {
        return this._activeRole.asObservable();
    }

    setActiveRole(value: string) {
        this._activeRole.next(value);
    }

    getActiveRole(): string | null {
        return this._activeRole.getValue();
    }

    generateTenantId(stateShortCode: string) {
        if (!stateShortCode) {
            return 'dynamic_lokos';
        }
        return `dynamic_lokos_${stateShortCode}`;
    }

    getSubRoles(id: string): Observable<string[]> {
        const url = `${this.keycloakApiBaseUrl}/api/keycloak/v2/graph/realms/${environment.keycloak.realm}/permission/${id}`;
        return this.http.get<string[]>(url);
    }

    getUserGeoLocation(role: string, geoLocation: 'national'): INationalPermission[];
    getUserGeoLocation(role: string, geoLocation: 'state'): IStatePermission[];
    getUserGeoLocation(role: string, geoLocation: 'district'): IDistrictPermission[];
    getUserGeoLocation(role: string, geoLocation: 'block'): IBlockPermission[];

    getUserGeoLocation(role: string, geoLocation: 'national' | 'state' | 'district' | 'block') {
        switch (geoLocation) {
            case 'national': {
                const nationalStateList =
                    this.storageService.getLocalItem<INationalPermission[]>(LOCAL_STORAGE_KEYS.NATIONAL_PERMISSIONS) ||
                    [];

                return nationalStateList;
            }

            case 'state': {
                const stateList =
                    this.storageService.getLocalItem<IStatePermission[]>(LOCAL_STORAGE_KEYS.STATE_PERMISSIONS) || [];

                return stateList.filter((state) => state.role === role);
            }

            case 'district': {
                const districtList =
                    this.storageService.getLocalItem<IDistrictPermission[]>(LOCAL_STORAGE_KEYS.DISTRICT_PERMISSIONS) ||
                    [];

                return districtList.filter((district) => district.role === role);
            }

            case 'block': {
                const blockList =
                    this.storageService.getLocalItem<IBlockPermission[]>(LOCAL_STORAGE_KEYS.BLOCK_PERMISSIONS) || [];

                return blockList.filter((block) => block.role === role);
            }

            default:
                return [];
        }
    }
}
